import * as THREE from "three";
import type { nivelReservatorio } from "./nivelReservatorio";

type Nivel = ReturnType<typeof nivelReservatorio>;

function etiqueta(nome: string) {
  const canvas = document.createElement("canvas"); canvas.width = 512; canvas.height = 100;
  const textura = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: textura, transparent: true, depthTest: false, toneMapped: false }));
  sprite.name = nome; sprite.scale.set(1.8, .35, 1); sprite.renderOrder = 6;
  let anterior = "";
  return { sprite, escrever(texto: string) {
    if (texto === anterior) return;
    anterior = texto;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, 512, 100); ctx.fillStyle = "rgba(10,15,19,.92)"; ctx.fillRect(0, 0, 512, 100);
    ctx.fillStyle = "#d7e2e7"; ctx.font = "bold 40px sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(texto, 256, 50); textura.needsUpdate = true;
  } };
}

/** Água e régua independentes do casco sólido da biblioteca do Mapa de Comando. */
export function montarVisualReservatorio(modelo: THREE.Object3D, elevado: boolean) {
  const fundo = elevado ? 9.3 : .25, altura = elevado ? 4.15 : 3.55;
  const casca: THREE.Material[] = [];
  modelo.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    for (const m of Array.isArray(o.material) ? o.material : [o.material]) if (/branco_tanque|faixa_|cinza_teto/.test(m.name)) {
      m.transparent = true; m.depthWrite = false; casca.push(m);
    }
  });
  const grupo = new THREE.Group(); grupo.name = "indicador_nivel_scada";
  const barra = new THREE.Mesh(new THREE.BoxGeometry(.1, 1, .1).translate(0, .5, 0), new THREE.MeshBasicMaterial({ color: "#2f9ee8" }));
  barra.name = "barra_nivel_atual";
  const trilho = new THREE.Mesh(new THREE.BoxGeometry(.06, altura, .06), new THREE.MeshStandardMaterial({ color: "#72848e" }));
  trilho.position.y = altura / 2;
  grupo.position.set(elevado ? 3.0 : 3.15, fundo, 0);
  grupo.add(trilho, barra);
  for (let i = 0; i <= 10; i++) {
    const marca = new THREE.Mesh(new THREE.BoxGeometry(i % 5 === 0 ? .22 : .12, .018, .04), new THREE.MeshBasicMaterial({ color: "#c3d8e5" }));
    marca.position.set(.07, altura * i / 10, 0); grupo.add(marca);
  }
  const atual = etiqueta("rotulo_nivel_atual"), topo = etiqueta("referencia_nivel"), zero = etiqueta("zero_nivel");
  topo.sprite.position.set(.75, altura + .3, 0); zero.sprite.position.set(.65, -.25, 0);
  grupo.add(atual.sprite, topo.sprite, zero.sprite);
  const agua = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshPhysicalMaterial({ color: "#168ed1", emissive: "#07395b",
    emissiveIntensity: .25, roughness: .18, metalness: .05, transparent: true, opacity: .9, side: THREE.DoubleSide }));
  agua.name = "agua_atual_scada"; agua.position.y = fundo;
  modelo.add(grupo, agua);
  let ultimaFracao: number | null = null;
  const fmt = (n: number) => n.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  return {
    atualizar(n: Nivel, distancia: number) {
      const visivel = n.fracao !== null;
      agua.visible = visivel && n.fracao! > 0;
      barra.visible = visivel;
      barra.scale.y = altura * (n.fracao ?? 0);
      if (n.fracao !== ultimaFracao) {
        ultimaFracao = n.fracao;
        const h = altura * (n.fracao ?? 0), pontos = [new THREE.Vector2(0, 0)];
        if (elevado) {
          pontos.push(new THREE.Vector2(.58, 0));
          if (h > 1.6) pontos.push(new THREE.Vector2(2.48, 1.6));
          pontos.push(new THREE.Vector2(h <= 1.6 ? .58 + 1.9 * h / 1.6 : 2.48, h));
        } else pontos.push(new THREE.Vector2(2.1, 0), new THREE.Vector2(2.1, h));
        pontos.push(new THREE.Vector2(0, h));
        agua.geometry.dispose(); agua.geometry = new THREE.LatheGeometry(pontos, 48);
      }
      const opacidade = visivel ? THREE.MathUtils.lerp(.28, 1, THREE.MathUtils.smoothstep(distancia, 100, 350)) : 1;
      for (const m of casca) { m.opacity = opacidade; m.depthWrite = opacidade >= .99; }
      atual.sprite.visible = topo.sprite.visible = zero.sprite.visible = distancia < 250;
      atual.sprite.position.set(1.0, altura * (n.fracao ?? .5), 0);
      atual.escrever(n.metros !== null ? `${fmt(n.metros)} m` : n.percentual !== null ? `${fmt(n.percentual)} %` : "Sem leitura recente");
      topo.escrever(n.maximo !== null ? `${n.referencia}: ${fmt(n.maximo)} m` : n.percentual !== null ? "100 %" : "Sem referência de altura");
      zero.escrever(n.maximo !== null ? "0 m" : "0 %");
    },
    destruir() {
      modelo.remove(grupo, agua);
      for (const raiz of [grupo, agua]) raiz.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
        const materiais = m.material ? Array.isArray(m.material) ? m.material : [m.material] : [];
        for (const mat of materiais) { (mat as THREE.SpriteMaterial).map?.dispose(); mat.dispose(); }
      });
    },
  };
}
